import {useCallback, useEffect, useMemo, useState} from 'react';
import {Form} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {
  GET_MANUAL_CONTENT_TRANSFORMATION,
  GET_MANUAL_CONTENT_TRANSFORMATIONS,
  TRIGGER_CONTENT_TRANSFORMATION,
} from 'graphql/queries/contentTransformations';
import {
  DEFAULT_PAGE_SIZE,
  POLL_INTERVAL_MS,
  SEARCH_DEBOUNCE_MS,
} from './constants';
import {
  errorCodeOf,
  errorMessageOf,
  isTerminal,
  languageLabel,
  nullIfBlank,
  pluralise,
} from './utils';

export const useContentTransformations = ({enabled = true} = {}) => {
  const [form] = Form.useForm();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);

  const [createOpen, setCreateOpen] = useState(false);
  const [formError, setFormError] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [detailsFallback, setDetailsFallback] = useState(null);

  /* ---- search: debounced, server-side, always back to page 1 ---- */
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  /* ---- history list ---- */
  const listQuery = useQuery(GET_MANUAL_CONTENT_TRANSFORMATIONS, {
    variables: {
      search: debouncedSearch || null,
      pagination: {page, limit},
    },
    skip: !enabled,
    // Statuses move underneath us, so never render a purely cached list.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const rows = useMemo(
    () =>
      (listQuery.data?.adminGetManualContentTransformations?.data || []).filter(
        Boolean,
      ),
    [listQuery.data],
  );

  // Trust the server's pagination block rather than counting rows.
  const pagination =
    listQuery.data?.adminGetManualContentTransformations?.pagination || null;

  const hasActiveRuns = useMemo(
    () => rows.some(row => !isTerminal(row.status)),
    [rows],
  );

  const {startPolling, stopPolling} = listQuery;
  useEffect(() => {
    if (enabled && hasActiveRuns) {
      startPolling(POLL_INTERVAL_MS);
      return () => stopPolling();
    }
    stopPolling();
    return undefined;
  }, [enabled, hasActiveRuns, startPolling, stopPolling]);

  const {refetch} = listQuery;
  const refetchHistory = useCallback(() => {
    if (enabled) refetch();
  }, [enabled, refetch]);

  /* ---- details ---- */
  const detailsQuery = useQuery(GET_MANUAL_CONTENT_TRANSFORMATION, {
    variables: {id: detailsId},
    skip: !enabled || !detailsId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    onError: error => {
      if (errorCodeOf(error) === 'CONTENT_TRANSFORMATION_NOT_FOUND') {
        setDetailsId(null);
        setDetailsFallback(null);
        toast.error('This content transformation could not be found.');
        return;
      }
      toast.error(
        errorMessageOf(error) || 'Could not load this content transformation.',
      );
    },
  });

  const openDetails = record => {
    // Show what the row already knows while the single resolver is in flight.
    setDetailsFallback(record);
    setDetailsId(record?.id || null);
  };

  const closeDetails = () => {
    setDetailsId(null);
    setDetailsFallback(null);
  };

  /* ---- creation ---- */
  const openCreate = () => {
    setFormError(null);
    setCreateOpen(true);
  };

  // Reset before the drawer unmounts (it uses destroyOnClose) so the next open
  // starts from the defaults rather than the previous submission.
  const closeCreate = () => {
    form.resetFields();
    setFormError(null);
    setCreateOpen(false);
  };

  /**
   * Back to page 1 and reload. `refetch` still carries the previous page in
   * its variables, so only fire it when the page number is not already
   * changing — otherwise the state change triggers the fetch on its own.
   */
  const resetToFirstPageAndReload = () => {
    if (page === 1) refetchHistory();
    else setPage(1);
  };

  // No retry link and no automatic resubmission: the server mints the
  // requestId, so a replay would start a second set of workflow runs.
  const [triggerTransformation, {loading: submitting}] = useMutation(
    TRIGGER_CONTENT_TRANSFORMATION,
    {errorPolicy: 'none'},
  );

  const submit = async values => {
    setFormError(null);
    const languages = values.languages || [];
    const input = {
      title: nullIfBlank(values.title),
      content: (values.content || '').trim(),
      sourceUrl: nullIfBlank(values.sourceUrl),
      languages,
      channels: values.channels || [],
    };

    try {
      const {data} = await triggerTransformation({variables: {input}});
      const payload = data?.adminTriggerContentTransformation;
      const started = payload?.dispatches?.length || languages.length;

      toast.success(
        `${pluralise(started, 'workflow run')} started${
          languages.length
            ? ` for ${languages.map(languageLabel).join(' and ')}`
            : ''
        }`,
      );
      closeCreate();
      resetToFirstPageAndReload();
      return payload;
    } catch (error) {
      const code = errorCodeOf(error);
      const message = errorMessageOf(error);

      if (code === 'BAD_USER_INPUT') {
        setFormError(message || 'Please check the values you submitted.');
      } else if (code === 'CONTENT_TRANSFORMATION_WORKFLOW_ERROR') {
        toast.error(
          'The workflow could not be started. Refresh history before trying again.',
        );
      } else {
        toast.error(message || 'The workflow could not be started.');
      }

      // Languages are dispatched sequentially, so an error can still leave
      // successfully dispatched records behind (e.g. English DISPATCHED,
      // French FAILED). Refetch so the admin sees the real outcome.
      resetToFirstPageAndReload();
      return null;
    }
  };

  const handlePageChange = (nextPage, nextLimit) => {
    setPage(nextPage);
    if (nextLimit && nextLimit !== limit) setLimit(nextLimit);
  };

  return {
    /* search */
    search,
    setSearch,
    isSearching: Boolean(debouncedSearch),
    clearSearch: () => setSearch(''),

    /* list */
    rows,
    pagination,
    listLoading: listQuery.loading,
    // networkStatus 4 = an explicit refetch (the Refresh button), as opposed to
    // the background poll (6) which should not spin any control.
    refreshing: listQuery.networkStatus === 4,
    listError: listQuery.error,
    refetchHistory,
    page,
    limit,
    handlePageChange,

    /* create */
    form,
    createOpen,
    openCreate,
    closeCreate,
    submitting,
    formError,
    submit,

    /* details */
    detailsOpen: Boolean(detailsId),
    detailsRecord:
      detailsQuery.data?.adminGetManualContentTransformation || detailsFallback,
    detailsLoading: detailsQuery.loading,
    openDetails,
    closeDetails,
  };
};
